import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ordo/ui/card'
import { Checkbox } from '@ordo/ui/checkbox'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { SimpleDialog } from '@ordo/ui/simple-dialog'
import { toast } from '@ordo/ui/sonner'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, CreditCard, Lock, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  type BillingFamilyAccount,
  type BillingPaymentMethod,
  type PaymentMethodVisibility,
  useBillingConfigQuery,
  useCreatePaymentMethodMutation,
  useCreateSetupIntentMutation,
  useRemovePaymentMethodMutation,
  useSetCardSharingMutation,
  useSetDefaultPaymentMethodMutation,
  useShareableGuardiansQuery,
} from '@/api/billing-family'
import { COMPACT_TAG_CLASS, COMPACT_TAG_WITH_ICON_CLASS } from '@/lib/tag-classes'

import { validateTestCard } from './helpers'
import { StripeElementsForm } from './stripe-elements-lazy'

const BRAND_OPTIONS = [
  { label: 'Visa', value: 'visa' },
  { label: 'Mastercard', value: 'mastercard' },
  { label: 'American Express', value: 'amex' },
  { label: 'Discover', value: 'discover' },
]

export function PaymentMethodsCard({
  account,
  openAddOnMount = false,
  onAddOpened,
}: {
  account: BillingFamilyAccount
  /** Start the add flow on arrival — the payment-failure email links here. */
  openAddOnMount?: boolean
  onAddOpened?: () => void
}) {
  const fundingEnabled = account.fundingEnabled !== false
  const [addOpen, setAddOpen] = useState(false)
  const [setupClientSecret, setSetupClientSecret] = useState<string | null>(null)
  const [brand, setBrand] = useState('visa')
  const [last4, setLast4] = useState('4242')
  const [expMonth, setExpMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'))
  const [expYear, setExpYear] = useState(String(new Date().getFullYear() + 3))
  // Step 2 of the Add-a-card modal. Private is the default: a card is yours
  // until you say otherwise.
  const [visibility, setVisibility] = useState<PaymentMethodVisibility>('PRIVATE')
  const [sharedWith, setSharedWith] = useState<string[]>([])
  // The local test-card dialog is TWO steps like the Stripe one: card fields,
  // then who may use it. The Stripe path keeps this state inside
  // StripeElementsForm; here the dialog owns it.
  const [testCardStep, setTestCardStep] = useState<1 | 2>(1)
  // Editing who may use an EXISTING card. Separate draft state from the add
  // flow so opening the editor can't disturb a half-finished add.
  const [sharingMethod, setSharingMethod] = useState<BillingPaymentMethod | null>(null)
  const [editVisibility, setEditVisibility] = useState<PaymentMethodVisibility>('PRIVATE')
  const [editSharedWith, setEditSharedWith] = useState<string[]>([])

  const qc = useQueryClient()
  const config = useBillingConfigQuery()
  const stripeConfigured = Boolean(config.data?.stripePublishableKey)
  const createPaymentMethodMutation = useCreatePaymentMethodMutation()
  const createSetupIntentMutation = useCreateSetupIntentMutation()
  const setDefaultMutation = useSetDefaultPaymentMethodMutation()
  const removeMutation = useRemovePaymentMethodMutation()
  const setSharingMutation = useSetCardSharingMutation()
  // Only fetched while an add-card modal is open — small, stable list. BOTH
  // paths need it: local dev (no Stripe key) uses the test-card dialog, and it
  // asks the same sharing question.
  const shareable = useShareableGuardiansQuery(
    account.id,
    Boolean(setupClientSecret) || addOpen || sharingMethod !== null,
  )
  const shareableGuardians = shareable.data?.guardians ?? []
  const loading =
    createPaymentMethodMutation.isPending ||
    createSetupIntentMutation.isPending ||
    setDefaultMutation.isPending ||
    removeMutation.isPending ||
    setSharingMutation.isPending

  function resetSharingChoice() {
    setVisibility('PRIVATE')
    setSharedWith([])
  }

  function closeAddCard() {
    setSetupClientSecret(null)
    resetSharingChoice()
  }

  function closeTestCard() {
    setAddOpen(false)
    setTestCardStep(1)
    resetSharingChoice()
  }

  /** Step 1 → 2. Validates the card fields without saving anything. */
  function handleTestCardNext() {
    const result = validateTestCard({ last4, expMonth, expYear }, new Date())
    if (!result.ok) {
      toast.error(result.message)
      return
    }
    setTestCardStep(2)
  }

  // Stripe configured → collect a real card via a SetupIntent + Elements.
  // Not configured (local dev without keys) → the test-card form below.
  const createSetupIntent = createSetupIntentMutation.mutateAsync
  const handleAddClick = useCallback(async () => {
    if (!fundingEnabled) {
      return
    }
    if (!stripeConfigured) {
      setAddOpen(true)
      return
    }
    try {
      const intent = await createSetupIntent({ accountId: account.id })
      if (!intent.clientSecret) {
        toast.error('Could not start card setup', { description: 'No setup session was returned.' })
        return
      }
      setSetupClientSecret(intent.clientSecret)
    } catch (err) {
      toast.error('Could not start card setup', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }, [fundingEnabled, stripeConfigured, createSetupIntent, account.id])

  // Arrived from the payment-failure email: start the add flow once, after the
  // billing config has loaded (it decides Stripe Elements vs the test card).
  const autoOpened = useRef(false)
  useEffect(() => {
    if (!fundingEnabled || !openAddOnMount || autoOpened.current || config.isPending) {
      return
    }
    autoOpened.current = true
    onAddOpened?.()
    void handleAddClick()
  }, [fundingEnabled, openAddOnMount, config.isPending, onAddOpened, handleAddClick])

  const handleSave = async () => {
    // Re-validated here, not trusted from step 1 — the fields stay editable via
    // Back, and this is the call that actually writes.
    const card = validateTestCard({ last4, expMonth, expYear }, new Date())
    if (!card.ok) {
      toast.error(card.message)
      setTestCardStep(1)
      return
    }
    try {
      const created = await createPaymentMethodMutation.mutateAsync({
        accountId: account.id,
        brand,
        last4: card.last4,
        expMonth: card.expMonth,
        expYear: card.expYear,
      })
      // Apply the sharing choice, same as the Stripe path does on success. The
      // row already exists here (no webhook in between), so it resolves by row
      // id. Without this the card would silently keep the schema default.
      try {
        await setSharingMutation.mutateAsync({
          accountId: account.id,
          paymentMethodRef: created.id,
          visibility,
          sharedWithUserIds: sharedWith,
        })
      } catch (err) {
        closeTestCard()
        toast.error("Card added, but who can use it wasn't applied", {
          description: err instanceof Error ? err.message : 'Set it from the card list.',
        })
        return
      }
      closeTestCard()
      toast.success('Payment method added')
    } catch (err) {
      toast.error('Could not add payment method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  function openSharingEditor(method: BillingPaymentMethod) {
    setSharingMethod(method)
    // Seed from the card's CURRENT state so the dialog opens on the truth rather
    // than a default. A legacy household-wide card (addedByUserId === null) has
    // visibility SHARED and an empty member list, so it opens on Shared with
    // nobody named — the user must either name people or switch it to Private,
    // which is exactly the claim-and-configure step the server expects.
    setEditVisibility(method.visibility)
    setEditSharedWith(method.sharedWithUserIds)
  }

  const handleSaveSharing = async () => {
    if (!sharingMethod) {
      return
    }
    try {
      await setSharingMutation.mutateAsync({
        accountId: account.id,
        paymentMethodRef: sharingMethod.id,
        visibility: editVisibility,
        sharedWithUserIds: editSharedWith,
      })
      setSharingMethod(null)
      toast.success('Updated who can use this card')
    } catch (err) {
      toast.error("Couldn't update who can use this card", {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handleSetDefault = async (method: BillingPaymentMethod) => {
    try {
      await setDefaultMutation.mutateAsync({ accountId: account.id, paymentMethodId: method.id })
      toast.success('Default payment method updated')
    } catch (err) {
      toast.error('Could not update default method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const handleRemove = async (method: BillingPaymentMethod) => {
    try {
      await removeMutation.mutateAsync({ accountId: account.id, paymentMethodId: method.id })
      toast.success('Payment method removed')
    } catch (err) {
      toast.error('Could not remove payment method', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  if (!fundingEnabled && account.paymentMethods.length === 0) {
    return null
  }

  return (
    <>
      <Card>
        <CardHeader>
          {/* CardTitle/CardDescription instead of a raw h3 + p: CardHeader owns
              the gap via space-y-1.5, and text-base matches the Members card so
              two cards on one page don't headline at different sizes (was text-lg).
              The Add button moved to the foot of the list — full-width at the top
              it was the loudest thing on the card, louder than the cards it adds to. */}
          <CardTitle className="text-base">Payment methods</CardTitle>
          {/* Describes the LIST below, which genuinely holds all four: bank
              accounts and Link get saved from the top-up sheet's "save this
              method" checkbox, not from Add below. Keep them named here. */}
          <CardDescription className="text-xs leading-relaxed">
            {fundingEnabled
              ? 'Saved cards, bank accounts, and Apple Pay / Google Pay.'
              : 'You can remove saved payment methods you no longer need.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col">
          {account.paymentMethods.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
              <CreditCard className="h-7 w-7 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium text-foreground">No payment methods saved yet</p>
              <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
                Add a card, Apple Pay, or Google Pay below — or save a bank account while topping up.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {/* Separated bordered rows with a gap, matching the Members card's
                  list idiom — the two cards sit on the same page and read as one
                  system. Two lines per row, same as a member row: what the card
                  is, then when it expires and who may use it. */}
              {account.paymentMethods.map((method) => {
                // Same gate as remove: the guardian who added a card owns its
                // sharing. A legacy card with no recorded adder is household-wide,
                // so any guardian may claim and configure it.
                const canEditSharing = method.addedByUserId === null || method.isMine
                return (
                  <div key={method.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                    {/* 32px to match the member avatars. */}
                    <div className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
                      <CreditCard className="h-4 w-4" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <p className="min-w-0 truncate text-sm font-medium text-foreground">
                          {formatPaymentMethodLabel(method)}
                        </p>
                        {method.isDefault ? (
                          <Badge variant="secondary" className={COMPACT_TAG_WITH_ICON_CLASS}>
                            <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
                            Default
                          </Badge>
                        ) : null}
                      </div>
                      {/* Expiry and access on one muted line — both are things you
                          confirm, neither is what you scan for. */}
                      <div className="flex min-w-0 items-center gap-1.5">
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {method.expMonth && method.expYear
                            ? `Expires ${String(method.expMonth).padStart(2, '0')}/${method.expYear}`
                            : toTitleCase(method.type)}
                        </span>
                        <span className="text-xs text-muted-foreground" aria-hidden>
                          ·
                        </span>
                        {/*
                          Who may charge this card. `addedByUserId === null` is a
                          card saved before sharing existed — household-wide, and
                          labelled as such rather than claiming a setting it
                          doesn't have.
                        */}
                        {method.addedByUserId === null ? (
                          <Badge variant="outline" className={COMPACT_TAG_CLASS}>
                            Everyone
                          </Badge>
                        ) : method.visibility === 'PRIVATE' ? (
                          <Badge variant="outline" className={COMPACT_TAG_WITH_ICON_CLASS}>
                            <Lock className="h-2.5 w-2.5" aria-hidden />
                            {method.isMine ? 'Only you' : 'Private'}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className={COMPACT_TAG_WITH_ICON_CLASS}>
                            <Users className="h-2.5 w-2.5" aria-hidden />
                            Shared
                            {method.sharedWithUserIds.length > 0 ? ` · ${method.sharedWithUserIds.length + 1}` : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {/* size xs — a compact row needs compact controls; the old
                        size="sm" (h-9) button was setting the row's height. */}
                    <div className="flex shrink-0 items-center gap-1">
                      {fundingEnabled && !method.isDefault ? (
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={() => void handleSetDefault(method)}
                          disabled={loading}>
                          Make default
                        </Button>
                      ) : null}
                      {fundingEnabled && canEditSharing ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => openSharingEditor(method)}
                          disabled={loading}
                          aria-label={`Change who can use ${formatPaymentMethodLabel(method)}`}>
                          <Pencil className="h-3 w-3" aria-hidden />
                        </Button>
                      ) : null}
                      {/* Only the guardian who added a card may remove it. */}
                      {canEditSharing ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={() => void handleRemove(method)}
                          disabled={loading}
                          aria-label={`Remove ${formatPaymentMethodLabel(method)}`}>
                          <Trash2 className="h-3 w-3" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Dashed foot-of-list affordance, same as the Members card. Demoted to
              text-xs: an add action is subordinate to the content it appends to. */}
          {fundingEnabled ? (
            <button
              type="button"
              onClick={() => void handleAddClick()}
              disabled={loading}
              className="mt-1.5 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/30 hover:text-foreground disabled:opacity-50">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Add payment method
            </button>
          ) : null}
        </CardContent>
      </Card>

      <SimpleDialog
        open={fundingEnabled && addOpen}
        onClose={closeTestCard}
        title="Add payment method"
        description="Add a test card for this local billing flow."
        maxWidth="md">
        <div className="flex flex-col gap-4">
          {/* Kept mounted, just hidden — same approach as the Stripe path, where
              unmounting would discard the card the user already typed. */}
          <div className={testCardStep === 2 ? 'hidden' : 'flex flex-col gap-4'}>
            <div className="flex flex-col gap-2">
              <Label htmlFor="payment-method-brand">Card brand</Label>
              <Select value={brand} onValueChange={setBrand} disabled={loading}>
                <SelectTrigger id="payment-method-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BRAND_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="payment-method-last4">Last 4</Label>
                <Input
                  id="payment-method-last4"
                  inputMode="numeric"
                  maxLength={4}
                  value={last4}
                  disabled={loading}
                  onChange={(event) => setLast4(event.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="payment-method-exp-month">Exp. month</Label>
                <Input
                  id="payment-method-exp-month"
                  inputMode="numeric"
                  maxLength={2}
                  value={expMonth}
                  disabled={loading}
                  onChange={(event) => setExpMonth(event.target.value.replace(/\D/g, '').slice(0, 2))}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="payment-method-exp-year">Exp. year</Label>
                <Input
                  id="payment-method-exp-year"
                  inputMode="numeric"
                  maxLength={4}
                  value={expYear}
                  disabled={loading}
                  onChange={(event) => setExpYear(event.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
            </div>
          </div>

          {/*
            Step 2 — the SAME sharing question the Stripe path asks. This dialog
            is the only add-card path when Stripe isn't configured (local dev), so
            without it a locally-added card silently takes the schema default and
            the feature can't be exercised at all here.
          */}
          {testCardStep === 2 ? (
            <CardSharingStep
              visibility={visibility}
              onVisibilityChange={setVisibility}
              guardians={shareableGuardians}
              selected={sharedWith}
              onToggle={(userId) =>
                setSharedWith((prev) =>
                  prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
                )
              }
            />
          ) : null}

          {/* Footer per step: Cancel / Next → on 1, Back / Add on 2 — the same
              two-step contract StripeElementsForm implements for real cards. */}
          <div className="flex justify-end gap-2">
            {testCardStep === 2 ? (
              <Button variant="outline" onClick={() => setTestCardStep(1)} disabled={loading}>
                Back
              </Button>
            ) : (
              <Button variant="outline" onClick={closeTestCard} disabled={loading}>
                Cancel
              </Button>
            )}
            {testCardStep === 1 ? (
              <Button onClick={handleTestCardNext} disabled={loading}>
                Next →
              </Button>
            ) : (
              <Button
                onClick={() => void handleSave()}
                disabled={loading || (visibility === 'SHARED' && sharedWith.length === 0)}>
                {loading ? 'Adding...' : 'Add payment method'}
              </Button>
            )}
          </div>
        </div>
      </SimpleDialog>

      {/* Change who can use an existing card. Reuses CardSharingStep so the
          question is worded and behaves identically to the add flow. */}
      <SimpleDialog
        open={sharingMethod !== null}
        onClose={() => setSharingMethod(null)}
        title="Who can use this card"
        description={sharingMethod ? formatPaymentMethodLabel(sharingMethod) : undefined}
        maxWidth="md">
        {sharingMethod ? (
          <div className="flex flex-col gap-4">
            <CardSharingStep
              visibility={editVisibility}
              onVisibilityChange={setEditVisibility}
              guardians={shareableGuardians}
              selected={editSharedWith}
              onToggle={(userId) =>
                setEditSharedWith((prev) =>
                  prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
                )
              }
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSharingMethod(null)} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSaveSharing()}
                disabled={loading || (editVisibility === 'SHARED' && editSharedWith.length === 0)}>
                {setSharingMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        ) : null}
      </SimpleDialog>

      <SimpleDialog
        open={Boolean(setupClientSecret)}
        onClose={closeAddCard}
        title="Add a payment method"
        description="Your card, Apple Pay, or Google Pay is saved securely by Stripe for the next time you add funds."
        maxWidth="md">
        {setupClientSecret ? (
          <StripeElementsForm
            clientSecret={setupClientSecret}
            mode="setup"
            submitLabel="Save payment method"
            secondStep={{
              nextLabel: 'Next →',
              canSubmit: visibility === 'PRIVATE' || sharedWith.length > 0,
              render: () => (
                <CardSharingStep
                  visibility={visibility}
                  onVisibilityChange={setVisibility}
                  guardians={shareableGuardians}
                  selected={sharedWith}
                  onToggle={(userId) =>
                    setSharedWith((prev) =>
                      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
                    )
                  }
                />
              ),
            }}
            onSuccess={(result) => {
              closeAddCard()
              // Apply the sharing choice against the card Stripe just returned.
              // The mirror row is created by the `payment_method.attached`
              // webhook, so the server resolves by the pm_… id.
              const pmId = result?.paymentMethodId ?? null
              if (pmId) {
                setSharingMutation.mutate(
                  { accountId: account.id, paymentMethodRef: pmId, visibility, sharedWithUserIds: sharedWith },
                  {
                    onError: (err) =>
                      toast.error("Card saved, but who can use it wasn't applied", {
                        description: err instanceof Error ? err.message : 'Set it from the card list.',
                      }),
                  },
                )
              }
              toast.success('Payment method saved', { description: 'It will appear here in a moment.' })
              void qc.invalidateQueries({ queryKey: ['billing', 'family'] })
            }}
            onCancel={closeAddCard}
          />
        ) : null}
      </SimpleDialog>
    </>
  )
}

/**
 * Step 2 of Add-a-card: who may charge it.
 *
 * All household cards live under one Stripe customer, so without this choice
 * any guardian could spend on this card. Private is the default.
 */
function CardSharingStep({
  visibility,
  onVisibilityChange,
  guardians,
  selected,
  onToggle,
}: {
  visibility: PaymentMethodVisibility
  onVisibilityChange: (next: PaymentMethodVisibility) => void
  guardians: Array<{ id: string; firstName: string; lastName: string; preferredName: string | null }>
  selected: string[]
  onToggle: (userId: string) => void
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-sm font-medium text-foreground">Who can use this card?</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Everyone on the household can add funds — this controls which card they can use.
        </p>
      </div>
      <RadioGroup
        value={visibility}
        onValueChange={(value) => onVisibilityChange(value as PaymentMethodVisibility)}
        className="flex flex-col gap-3">
        <Label
          htmlFor="card-visibility-private"
          className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <RadioGroupItem id="card-visibility-private" value="PRIVATE" className="mt-0.5" />
          <span className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">Private</span>
            <span className="text-xs text-muted-foreground">Only you can add funds with this card.</span>
          </span>
        </Label>
        <Label
          htmlFor="card-visibility-shared"
          className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <RadioGroupItem id="card-visibility-shared" value="SHARED" className="mt-0.5" />
          <span className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">Shared</span>
            <span className="text-xs text-muted-foreground">
              Chosen household members can add funds with this card too.
            </span>
          </span>
        </Label>
      </RadioGroup>

      {visibility === 'SHARED' ? (
        guardians.length === 0 ? (
          <p className="rounded-md border border-dashed border-border/60 bg-muted/20 p-3 text-sm text-muted-foreground">
            There is nobody else on this household yet. Add a member first, or keep this card private.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-foreground">Share with</p>
            {guardians.map((guardian) => {
              const name = guardian.preferredName?.trim() || `${guardian.firstName} ${guardian.lastName}`.trim()
              return (
                <Label
                  key={guardian.id}
                  htmlFor={`card-share-${guardian.id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                  <Checkbox
                    id={`card-share-${guardian.id}`}
                    checked={selected.includes(guardian.id)}
                    onCheckedChange={() => onToggle(guardian.id)}
                  />
                  <span className="text-sm text-foreground">{name}</span>
                </Label>
              )
            })}
            {selected.length === 0 ? (
              <p className="text-xs text-muted-foreground">Pick at least one person, or choose Private.</p>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  )
}

function formatPaymentMethodLabel(method: BillingPaymentMethod): string {
  if (method.walletType === 'apple_pay') {
    return method.last4 ? `Apple Pay ending ${method.last4}` : 'Apple Pay'
  }
  if (method.walletType === 'google_pay') {
    return method.last4 ? `Google Pay ending ${method.last4}` : 'Google Pay'
  }
  const brand = method.brand ? toTitleCase(method.brand) : toTitleCase(method.type || 'card')
  return method.last4 ? `${brand} ending ${method.last4}` : brand
}

function toTitleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}
